import {
  App,
  getAppEnumBySpartenType,
  Hinweismeldung,
  StandaloneProdukt,
  Vorbelegungdaten,
  Vorvertrag,
} from '@ps20-frontend/frontend-models';
import {
  OperatingStandaloneVertragsdaten,
  OperatingStandardVertragsdaten,
  OperatingVertragsdaten,
} from '@ps20/bff-models';
import { AnyAction } from 'redux';
import { DashboardActions } from '../dashboard.actions';
import {
  buildStandaloneInfoText,
  getDashboardIcons,
  mapBffVorbelegungdaten,
  mapHinweisMeldungen,
  mapNeugeschaeftSperren,
  mapRisikoortAnzeige,
} from '../sagas/vorvertragsUebersichtSaga';
import { isStandaloneVertrag } from '../selectors/vorvertragsUebersicht.selector';
import { FremdVertraegeAendern } from '../../app/uebersicht/vertrag-auswahl-modal/fremdVertraegeAendern.interface';

export const fremdVertragReducer = function (
  sectionState: Array<StandaloneProdukt | Vorvertrag> = null,
  action: AnyAction,
): Array<StandaloneProdukt | Vorvertrag> {
  switch (action.type) {
    case DashboardActions.FREMD_VERTRAEGE_AENDERN: {
      sectionState = action.payload;
      return sectionState;
    }
    case DashboardActions.UPDATE_FREMDE_STANDALONEPRODUKTE: {
      const payload: Array<OperatingStandaloneVertragsdaten> = action.payload;
      return updateVertraege(payload, sectionState);
    }
    case DashboardActions.UPDATE_FREMDE_VORVERTRAEGE: {
      const payload: Array<OperatingStandardVertragsdaten> = action.payload;
      return updateVertraege(payload, sectionState);
    }
    default:
      return sectionState;
  }
};

export function returnBasicData(update: OperatingVertragsdaten): {
  eigenschaftHinweise: Hinweismeldung[];
  vorbelegungdaten: Vorbelegungdaten[];
  ersetzen: boolean;
} {
  return {
    ersetzen: update.ersetzen,
    eigenschaftHinweise: mapHinweisMeldungen(update.hinweismeldungen),
    vorbelegungdaten: mapBffVorbelegungdaten(update.vorbelegungdaten),
  };
}

export function updateVertraege(
  updates: Array<OperatingStandaloneVertragsdaten | OperatingStandardVertragsdaten>,
  sectionState: Array<StandaloneProdukt | Vorvertrag>,
): Array<StandaloneProdukt | Vorvertrag> {
  for (const update of updates) {
    const index = sectionState.findIndex(
      vertrag =>
        vertrag.spLaufnummer === update.spLaufnummer &&
        vertrag.vertragsnummer === update.vertragsnummer,
    );
    if (index >= 0) {
      if (isStandaloneVertrag(sectionState[index])) {
        sectionState[index] = updateStandaloneProdukt(
          update as OperatingStandaloneVertragsdaten,
          sectionState[index] as StandaloneProdukt,
        );
      } else {
        sectionState[index] = updateVorvertrag(
          update as OperatingStandardVertragsdaten,
          sectionState[index] as Vorvertrag,
        );
      }
    }
  }
  return sectionState;
}

export function updateVorvertrag(
  update: OperatingStandardVertragsdaten,
  vorvertrag: Vorvertrag,
): Vorvertrag {
  const updatedData = {
    ...returnBasicData(update),
    infoText: update.infoText,
    neugeschaeftSperren: mapNeugeschaeftSperren(update.neugeschaeftSperren),
    umstellungszwang: update.umstellungszwang,
    icon: getDashboardIcons(update.spartenTypes),
    risikoortInfo: null,
    beibehaltung: update.beibehaltung,
  } as Partial<Vorvertrag>;
  updatedData.risikoortInfo = mapRisikoortAnzeige(
    vorvertrag.produktIds,
    updatedData.ersetzen,
    updatedData.vorbelegungdaten,
  );
  return Object.assign(vorvertrag, updatedData);
}

export function updateStandaloneProdukt(
  update: OperatingStandaloneVertragsdaten,
  standaloneprodukt: StandaloneProdukt,
): StandaloneProdukt {
  const updatedData = {
    ...returnBasicData(update),
    einschliessenDetails: update.einschliessen.map(detail => ({
      appId: getAppEnumBySpartenType(detail.spartenType),
      pflichtZusatzoptionen: detail.pflichtZusatzoptionen,
    })),
    infoText: update.infoText ? update.infoText : buildStandaloneInfoText(update.einschliessen),
    einschliessenderVertrag: update.inProduktVorhanden ? update.inProduktVorhanden : [],
    risikoortInfo: null,
  } as Partial<StandaloneProdukt>;
  const appIds: App[] = updatedData.einschliessenDetails.map(einschl => einschl.appId);
  updatedData.risikoortInfo = mapRisikoortAnzeige(
    appIds,
    updatedData.ersetzen,
    updatedData.vorbelegungdaten,
  );
  return Object.assign(standaloneprodukt, updatedData);
}

export function updateFremdvertragState(
  payload: FremdVertraegeAendern,
  sectionState: Array<StandaloneProdukt | Vorvertrag>,
): Array<StandaloneProdukt | Vorvertrag> {
  sectionState = sectionState.filter(
    vertrag =>
      !payload.removeVertraege.some(
        removedVertrag =>
          removedVertrag.vertragsnummer === vertrag.vertragsnummer &&
          removedVertrag.spLaufnummer === vertrag.spLaufnummer,
      ),
  );
  return sectionState.concat(payload.addVertraege);
}
